import { useEndpointData } from "../api";
import type { Group } from "../types";

interface Props {
  group: Group;
  endpoint: string;
}

/** AboutPanel loads readonly metadata (usually /api/version) via
 *  useEndpointData — the same hook VersionBadge uses, so the two components
 *  share a single cache entry keyed on the endpoint URL. */
export function AboutPanel({ group, endpoint }: Props) {
  const q = useEndpointData<Record<string, unknown>>(endpoint);
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <header className="border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold text-card-foreground">{group.title}</h2>
        {group.description && (
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {group.description}
          </p>
        )}
      </header>
      <div className="px-6 py-4">
        {q.isPending && (
          <div className="text-sm text-muted-foreground">Loading…</div>
        )}
        {q.error && (
          <div className="text-sm text-destructive">{String(q.error)}</div>
        )}
        {q.data && (
          <dl className="grid grid-cols-[minmax(160px,200px)_1fr] gap-x-6 gap-y-2 text-sm">
            {Object.entries(q.data).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-mono text-muted-foreground">{k}</dt>
                <dd className="break-all font-mono text-card-foreground">
                  {typeof v === "object" && v !== null
                    ? JSON.stringify(v)
                    : String(v)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}
